/**
 * Mapa interativo de um ponto via Google Maps JavaScript API — usado no `ModalMapa` quando há
 * `VITE_GOOGLE_MAPS_API_KEY`. É a base para o rastreamento (mapa vivo, marcadores programáticos)
 * que virá; para um único endereço já entrega zoom/arraste reais, melhores que o iframe embed.
 *
 * Sem coordenadas, geocodifica o texto do endereço no próprio cliente. Qualquer falha (chave
 * inválida, rede, endereço não resolvido) chama `onFalhar`, e o modal cai no iframe keyless —
 * então o mapa nunca fica em branco.
 */
import { useEffect, useRef } from 'react';
import { garantirOpcoes, importLibrary, MAP_ID } from '@/lib/mapas';

export function MapaInterativo({
  apiKey,
  latitude,
  longitude,
  consultaTexto,
  titulo,
  onFalhar,
}: {
  apiKey: string;
  latitude?: number | null;
  longitude?: number | null;
  consultaTexto: string;
  titulo: string;
  onFalhar: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelado = false;
    garantirOpcoes(apiKey);

    async function montar() {
      const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
        importLibrary('maps'),
        importLibrary('marker'),
      ]);
      if (cancelado || !ref.current) return;

      let centro: google.maps.LatLngLiteral | null =
        latitude != null && longitude != null ? { lat: latitude, lng: longitude } : null;
      if (!centro) {
        const { Geocoder } = await importLibrary('geocoding');
        const res = await new Geocoder().geocode({ address: consultaTexto });
        const loc = res.results[0]?.geometry.location;
        if (loc) centro = { lat: loc.lat(), lng: loc.lng() };
      }
      if (cancelado || !ref.current) return;
      if (!centro) {
        onFalhar();
        return;
      }

      const mapa = new Map(ref.current, {
        center: centro,
        zoom: 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        // Sem Map ID o marcador moderno não renderiza — o mapa apareceria sem o pino.
        mapId: MAP_ID,
      });
      new AdvancedMarkerElement({ position: centro, map: mapa, title: titulo });
    }

    montar().catch(() => {
      if (!cancelado) onFalhar();
    });

    return () => {
      cancelado = true;
    };
  }, [apiKey, latitude, longitude, consultaTexto, titulo, onFalhar]);

  return <div ref={ref} className="absolute inset-0 size-full" />;
}
